import React, { useEffect, useState } from "react";
import { db } from "../../lib/firebase";
import { collection, doc, getDocs, setDoc, updateDoc } from "firebase/firestore";
import { Spinner } from "../../components/common/Spinner";
import { Button } from "../../components/common/Button";
import { Card } from "../../components/common/Card";
import { UserRole } from "../../types/user";
import { toast } from "react-hot-toast";
import { adminWalletOp, callableMessage } from "../../services/callables";

const roleOptions: UserRole[] = ["customer", "chef", "deliveryAgent", "admin", "cashier", "kitchenOwner"];

type UserSectionProps = {
  trigger?: number;
  onAdminEvent?: () => void;
};

// Utility to ensure standard phone format
const normalizePhone = (phone: string) => {
  let p = phone.replace(/\D/g, "");
  if (p.length === 10) return "91" + p;
  if (p.length === 11 && p.startsWith("0")) return "91" + p.substring(1);
  return p;
};

// Onboard new user + wallet
function UserOnboarding({ onAdminEvent }: { onAdminEvent?: () => void }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [roles, setRoles] = useState<UserRole[]>([]);
  const [loading, setLoading] = useState(false);

  const handleRoleChange = (role: UserRole) => {
    setRoles((r) => (r.includes(role) ? r.filter((x) => x !== role) : [...r, role]));
  };

  const handleOnboard = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const cleanPhone = normalizePhone(phone);

    try {
      const docRef = doc(collection(db, "users"));

      await setDoc(docRef, {
        uid: docRef.id,
        name,
        phone: cleanPhone,
        email,
        address,
        roles,
        currentRole: roles[0] || "customer",
        onboardingComplete: false,
        approved: false,
        createdAt: new Date(),
        walletBalance: 0,
        photoURL: "",
      });

      // Wallet docs are server-managed (rules block client writes)
      await adminWalletOp({ op: "ensure", walletId: docRef.id, ownerType: "user" });

      setName("");
      setPhone("");
      setEmail("");
      setAddress("");
      setRoles([]);
      toast.success("User created successfully");
      onAdminEvent && onAdminEvent();
    } catch (error) {
      console.error("Error adding user:", error);
      toast.error("Failed to create user");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="mb-6 p-4">
      <h3 className="font-bold text-lg mb-3 text-gray-800">Onboard New User</h3>
      <form onSubmit={handleOnboard} className="flex flex-col gap-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <input
            type="text"
            placeholder="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="form-control"
            required
          />
          <input
            type="text"
            placeholder="Phone (e.g. 9876543210)"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="form-control"
            required
          />
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="form-control"
          />
          <input
            type="text"
            placeholder="Address"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            className="form-control"
          />
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <span className="font-medium text-sm text-gray-700">Assign Roles:</span>
          <div className="flex flex-wrap gap-3">
            {roleOptions.map((role) => (
              <label
                key={role}
                className="flex items-center gap-1.5 cursor-pointer text-sm text-gray-600"
              >
                <input
                  type="checkbox"
                  checked={roles.includes(role)}
                  onChange={() => handleRoleChange(role)}
                  className="rounded text-orange-600 focus:ring-orange-500"
                />
                {role.charAt(0).toUpperCase() + role.slice(1)}
              </label>
            ))}
          </div>
        </div>
        <div className="flex justify-end">
          <Button type="submit" isLoading={loading}>
            {loading ? "Adding..." : "Create User & Wallet"}
          </Button>
        </div>
      </form>
    </Card>
  );
}

function UserList({
  onAdminEvent,
  trigger,
}: {
  onAdminEvent?: () => void;
  trigger?: number;
}) {
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState<any | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    walletBalance: 0,
    roles: [] as UserRole[],
    approved: false,
  });

  useEffect(() => {
    async function fetchUsers() {
      setLoading(true);
      try {
        const usersRef = collection(db, "users");
        const snapshot = await getDocs(usersRef);
        setUsers(snapshot.docs.map((d) => ({ ...d.data(), uid: d.id })));
      } catch (error) {
        console.error("Error fetching users:", error);
        toast.error("Failed to fetch users");
      } finally {
        setLoading(false);
      }
    }
    fetchUsers();
  }, [trigger]);

  useEffect(() => {
    if (editingUser) {
      setFormData({
        name: editingUser.name || "",
        phone: editingUser.phone || "",
        email: editingUser.email || "",
        address: editingUser.address || "",
        walletBalance: editingUser.walletBalance || 0,
        roles: editingUser.roles || [],
        approved: editingUser.approved || false,
      });
    }
  }, [editingUser]);

  const handleSaveUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingUser) return;
    setIsSaving(true);

    const cleanPhone = normalizePhone(formData.phone);

    try {
      const userRef = doc(db, "users", editingUser.uid);
      // Profile/role fields only; walletBalance is server-managed (mirrored from wallets/{uid}).
      const { walletBalance: newBalance, ...profileFields } = formData;
      await updateDoc(userRef, {
        ...profileFields,
        phone: cleanPhone,
      });

      const target = Number(newBalance);
      if (isFinite(target) && target !== Number(editingUser.walletBalance || 0)) {
        await adminWalletOp({
          op: "setBalance",
          walletId: editingUser.uid,
          balance: target,
          reason: "Admin edit (user section)",
          adjustGlobal: true,
        });
      }

      toast.success("User updated successfully");
      setEditingUser(null);
      onAdminEvent && onAdminEvent();
    } catch (err) {
      console.error(err);
      toast.error(callableMessage(err, "Failed to update user"));
    } finally {
      setIsSaving(false);
    }
  };

  const toggleEditRole = (role: UserRole) => {
    setFormData((prev) => ({
      ...prev,
      roles: prev.roles.includes(role)
        ? prev.roles.filter((r) => r !== role)
        : [...prev.roles, role],
    }));
  };

  if (loading) {
    return (
      <div className="py-10 flex justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <>
      <Card className="p-0 overflow-hidden">
        <div className="p-4 border-b border-gray-100 bg-gray-50">
          <h3 className="font-bold text-gray-800">User Registry</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead>
              <tr className="bg-gray-100 text-gray-600">
                <th className="p-3">Name</th>
                <th className="p-3">Contact</th>
                <th className="p-3">Roles</th>
                <th className="p-3">Wallet</th>
                <th className="p-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {users.map((u) => (
                <tr
                  key={u.uid}
                  className={`hover:bg-gray-50 transition-colors ${
                    u.roles?.includes("deliveryAgent") ? "bg-blue-50/30" : ""
                  }`}
                >
                  <td className="p-3 font-medium text-gray-900">
                    {u.name || "No Name"}
                    <div className="text-xs text-gray-400">{u.email}</div>
                  </td>
                  <td className="p-3 text-gray-600">
                    <div>{u.phone || "-"}</div>
                    <div className="text-xs text-gray-400 truncate max-w-[150px]">
                      {u.address}
                    </div>
                  </td>
                  <td className="p-3">
                    <div className="flex gap-1 flex-wrap">
                      {u.roles?.map((r: string) => (
                        <span
                          key={r}
                          className="text-xs px-2 py-0.5 bg-gray-200 rounded-full text-gray-700 capitalize"
                        >
                          {r}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="p-3 font-mono font-semibold text-green-600">
                    {u.walletBalance?.toFixed(2) ?? "0.00"}
                  </td>
                  <td className="p-3 text-center">
                    <Button
                      variant="secondary"
                      className="py-1 px-3 text-xs h-auto"
                      onClick={() => setEditingUser(u)}
                    >
                      Edit
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {editingUser && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex justify-center items-center p-4"
          onClick={() => setEditingUser(null)}
        >
          <form
            className="bg-white p-6 rounded-xl shadow-2xl max-w-md w-full flex flex-col gap-4"
            onClick={(e) => e.stopPropagation()}
            onSubmit={handleSaveUser}
          >
            <div className="border-b pb-2">
              <h3 className="text-xl font-bold text-gray-900">Edit User</h3>
              <p className="text-sm text-gray-500">ID: {editingUser.uid}</p>
            </div>
            <div className="grid grid-cols-1 gap-3">
              <input
                type="text"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                className="form-control"
                required
                placeholder="Name"
              />
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="text"
                  value={formData.phone}
                  onChange={(e) =>
                    setFormData({ ...formData, phone: e.target.value })
                  }
                  className="form-control"
                  required
                  placeholder="Phone"
                />
                <input
                  type="number"
                  step="0.01"
                  value={formData.walletBalance}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      walletBalance: Number(e.target.value),
                    })
                  }
                  className="form-control font-mono"
                  placeholder="Balance"
                />
              </div>
              <input
                type="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                className="form-control"
                placeholder="Email"
              />
              <textarea
                rows={2}
                value={formData.address}
                onChange={(e) =>
                  setFormData({ ...formData, address: e.target.value })
                }
                className="form-control"
                placeholder="Address"
              />
              <div>
                <label className="form-label text-xs mb-2 block">Roles</label>
                <div className="flex flex-wrap gap-3 bg-gray-50 p-2 rounded border border-gray-200">
                  {roleOptions.map((role) => (
                    <label
                      key={role}
                      className="flex items-center gap-1.5 cursor-pointer text-sm text-gray-700"
                    >
                      <input
                        type="checkbox"
                        checked={formData.roles.includes(role)}
                        onChange={() => toggleEditRole(role)}
                        className="rounded text-orange-600 focus:ring-orange-500"
                      />
                      {role.charAt(0).toUpperCase() + role.slice(1)}
                    </label>
                  ))}
                </div>
              </div>
              {formData.roles.includes("chef") && (
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="approved-check"
                    checked={formData.approved}
                    onChange={(e) =>
                      setFormData({ ...formData, approved: e.target.checked })
                    }
                    className="rounded text-green-600 focus:ring-green-500"
                  />
                  <label
                    htmlFor="approved-check"
                    className="text-sm font-medium text-gray-700"
                  >
                    Chef Approved?
                  </label>
                </div>
              )}
            </div>
            <div className="flex gap-3 mt-2 pt-2 border-t">
              <Button
                type="button"
                variant="secondary"
                className="flex-1"
                onClick={() => setEditingUser(null)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                className="flex-1"
                isLoading={isSaving}
              >
                Save Changes
              </Button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}

export const UserSection: React.FC<UserSectionProps> = ({ onAdminEvent, trigger }) => (
  <div className="mb-12">
    <UserOnboarding onAdminEvent={onAdminEvent} />
    <UserList onAdminEvent={onAdminEvent} trigger={trigger} />
  </div>
);
